import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Flex, Heading, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { FiArrowLeft } from "react-icons/fi";
import { IconType } from "react-icons";
import { alpha, useCalTheme } from "./useCalTheme";
import { FadeIn } from "./motion";
import { CalTip } from "./CalTip";

interface Props {
  icon: IconType;
  eyebrow?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Chips next to the title — status, token, etc. */
  badges?: React.ReactNode;
  actions?: React.ReactNode;
  back?: { to: string; label: string };
}

/**
 * Every calendars page opens the same way: where you are (eyebrow), what this is (title + icon tile in
 * the brand gradient), one line on why it matters, and the page's main action on the right. A soft glow
 * sits behind it on dark — decoration that never carries meaning.
 */
export function CalPageHeader({ icon, eyebrow, title, subtitle, badges, actions, back }: Props) {
  const t = useCalTheme();
  return (
    <FadeIn y={8}>
      <Box position="relative" pb={1}>
        <Box aria-hidden position="absolute" top="-40px" left="-30px" w="260px" h="180px" bgImage={t.accentGradient}
          opacity={t.colorMode === "dark" ? 0.18 : 0.08} filter="blur(60px)" borderRadius="full" pointerEvents="none" />
        {back && (
          <CalTip label={`Back to ${back.label}`}>
            {/* blur-exempt: navigates away */}
            <Button as={RouterLink} to={back.to} size="md" variant="ghost" leftIcon={<Icon as={FiArrowLeft} />} mb={2} ml={-2} minH="44px" color={t.muted}>
              {back.label}
            </Button>
          </CalTip>
        )}
        <Stack direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "stretch", md: "center" }} spacing={4} position="relative">
          <HStack spacing={4} align="center" minW={0}>
            <Flex flexShrink={0} w={{ base: 12, md: 14 }} h={{ base: 12, md: 14 }} borderRadius="xl" bgImage={t.accentGradient}
              align="center" justify="center" boxShadow={`0 8px 24px ${alpha(t.primary, 0.35)}`}>
              <Icon as={icon} boxSize={{ base: 6, md: 7 }} color="white" />
            </Flex>
            <Box minW={0}>
              {eyebrow && <Text fontSize="sm" fontWeight="semibold" color={t.faint}>{eyebrow}</Text>}
              <HStack spacing={2} flexWrap="wrap" align="center">
                <Heading as="h1" size={{ base: "md", md: "lg" }} letterSpacing="-0.02em" color={t.text} wordBreak="break-word">{title}</Heading>
                {badges}
              </HStack>
              {subtitle && <Text fontSize="md" color={t.muted} mt={1} maxW="62ch">{subtitle}</Text>}
            </Box>
          </HStack>
          {actions && <HStack spacing={2} flexWrap="wrap" justify={{ base: "flex-start", md: "flex-end" }} flexShrink={0}>{actions}</HStack>}
        </Stack>
      </Box>
    </FadeIn>
  );
}
